import { html, node } from 'wirejs-dom/v2'
import { CodeBlock } from './code-block.js';

export const monitorSvg = /* svg */ `<svg
	style='width: 100%; height: auto;'
	viewBox="0 0 370 250"
	xmlns="http://www.w3.org/2000/svg"
>
	<!-- Monitor body -->
	<rect x="0" y="0" width="370" height="250" rx="10" ry="10"
		fill="#f5f5dc" stroke="#bfbfa3" stroke-width="3" />

	<!-- Screen -->
	<rect x="25" y="25" width="320" height="200" rx="5" ry="5"
		fill="#000" stroke="#000" stroke-width="2" />

	<!-- Brand Name -->
	<text x="265" y="240"
		font-family="monospace" font-size="10" fill="#555"
	>Interweb VGA</text>

	<!-- Knobs, Buttons, and Light -->
	<circle class='powerButton'
		cx="55" cy="236" r="4" fill="#888" />
	<line class='powerButton'
		x1="55" y1="234" x2="55" y2="238"
		stroke="#555" stroke-width="1" />

	<circle cx="70" cy="236" r="4" fill="#888"/>
	<rect x="35" y="232" width="8" height="8" rx="1" fill="#6a6"/>

	<!-- FullScreen Button -->
	<text class="fullscreenButton"
		x="348" y="239" font-size="11"
		style="cursor: pointer;" fill="#999"
	>⛶</text>
</svg>`;

const Canvas = (options: {
	resolution: { width: number; height: number };
}) => {
	return html`<canvas
		id='canvas'
		width='${options.resolution.width}'
		height='${options.resolution.height}'
		style='
			width: 70%;
			height: auto;
			display: block;
			background-color: black;
			position: absolute;
			top: 10.5%;
			left: 7.5%;
			width: 85%;
			height: 77.5%;
		'
	></canvas>`;
};

type DosMonitorOptions = {
	resolution?: { width: number; height: number };
	settings?: object;
};

export const DosMonitor = <const T extends DosMonitorOptions>(options?: T) => {
	let ctx: CanvasRenderingContext2D | null = null;

	const resolution = (options?.resolution || { width: 320, height: 200 }) as
		Exclude<T['resolution'], undefined>;
	const settings = (options?.settings || {}) as T['settings'];

	return html`<div>
		${node('code', code => code
			? html`<div>
				<b>Code:</b>
				${CodeBlock({ code, language: 'javascript'})}
				<b>Result:</b>
			<div>`
			: html`<span></span>`
		)}
		<div id='monitor' style='
			width: 70%;
			margin: 0 auto;
			display: block;
			position: relative;
		'>
			${monitorSvg}
			${node('canvas', Canvas({ resolution }) as any as HTMLCanvasElement)}
		</div>
	</div>`.extend(self => ({
		options: { resolution, settings },
		draw: {
			get context() {
				ctx = ctx ?? self.data.canvas.getContext('2d')!;
				ctx.imageSmoothingEnabled = false;
				return ctx;
			},
			point(x: number, y: number, color: string) {
				const ctx = this.context;
				ctx.fillStyle = color;
				ctx.fillRect(Math.floor(x), Math.floor(y), 1, 1);
			},
			line(x1: number, y1: number, x2: number, y2: number, color: string) {
				const ctx = this.context;
				ctx.strokeStyle = color;
				ctx.lineWidth = 1;
				ctx.beginPath();
				ctx.moveTo(Math.floor(x1), Math.floor(y1));
				ctx.lineTo(Math.floor(x2), Math.floor(y2));
				ctx.stroke();
			},
			text(text: string, x: number, y: number, color: string = '#fff') {
				const ctx = this.context;
				ctx.fillStyle = color;
				ctx.font = '9px monospace';
				ctx.fontKerning = 'none'
				ctx.fillText(text, x, y);
			},
			set imageData(imageData: ImageData) {	
				const ctx = this.context;
				ctx.canvas.width = imageData.width;
				ctx.canvas.height = imageData.height;
				ctx.putImageData(imageData, 0, 0);
			},
			get imageData() {
				return this.context.getImageData(
					0, 0,
					this.context.canvas.width,
					this.context.canvas.height
				);
			},
		},
		program(fn: (s: ReturnType<typeof DosMonitor<T>>) => void) {
			self.data.code = fn.toString();
			return self.onadd(fn as any);
		},
		onPowerButton(cb: () => void) {
			const buttons = self.getElementsByClassName('powerButton');
			for (const button of buttons) {
				(button as HTMLElement).style.cursor = 'pointer';
				button.addEventListener('click', cb);
			}
		},
	})).onadd(self => {
		self.draw.context.fillStyle = 'black';
		self.draw.context.fillRect(
			0, 0,
			self.draw.context.canvas.width,
			self.draw.context.canvas.height
		);

		const svgElement = self.querySelector('svg')!;
		const setSize = () => {
			const aspectRatio = svgElement.viewBox.baseVal.height / svgElement.viewBox.baseVal.width;
			svgElement.style.height = `${svgElement.clientWidth * aspectRatio}px`;
		}

		setSize();
		window.addEventListener('resize', setSize);

		self.getElementsByClassName('fullscreenButton')[0].addEventListener(
			'click',
			() => self.data.canvas.requestFullscreen()
		);
	});
}